
import LocationAddress from '@/models/LocationAddress';
import { Address, Location, Trip, TripMutation, TripStop } from '@clients/myTrips';
import IconAdd from '@components/icons/icon-add.svg';
import IconMinus from '@components/icons/icon-minus.svg';
import AddressInputField from '@components/new-styleguide/form/AddressInputField.vue';
import TripDetailDataLine from '@components/trip-detail/trip-data/TripDetailDataLine.vue';
import addressFormat from '@util/addressFormat';
import deepCopy from '@util/copy';
import { featureToAddress, featureToLocation } from '@util/mapbox';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const GeographicsModule = namespace('Geographics');

const stopsLimit = 5;

@Component({
  name: 'TripDataStops',
  components: {
    AddressInputField,
    TripDetailDataLine,
    IconMinus,
    IconAdd
  }
})

export default class TripDataStops extends Vue {
  @Prop()
  private trip: Trip;

  @Prop()
  private tripMutation: TripMutation;

  @Prop({ default: false })
  private loading: boolean;

  @GeographicsModule.Action('fetchPlaceNames')
  private fetchPlaceNames;

  // SHORT NOTE: null = empty address, undefined = placeholder for "add" field
  private stops: Array<TripStop> = [];

  created() {
    this.updateStops();
  }

  @Watch('tripMutation')
  private watchTripMutation() {
    this.updateStops();
  }

  get tripStops() {
    if (this.loading) {
      return [];
    }
    if (!this.editMode) {
      return this.trip.stops;
    }
    if (this.stops.length >= stopsLimit) {
      return this.stops;
    }
    return [...this.stops, undefined];
  }

  get editMode() {
    return !!this.tripMutation;
  }

  private handleClickButton(stop) {
    if (stop === undefined) {
      this.handleAddAddress();
    }
  }

  public focusField(field: string) {
    const num = +field.replace('stop-' as string, '');
    this.$refs.stop[num].focusField();
  }

  private getAddress(address: Address) {
    return addressFormat(address);
  }

  private title(trip: TripStop) {
    return trip && trip.address ? this.getAddress(trip.address) : '';
  }

  private tripStopToLocationAddress(stop: TripStop): LocationAddress {
    return stop ? { address: stop.address, location: stop.location } : undefined;
  }

  private locationAddressToTripStop(la: LocationAddress): TripStop {
    return { address: la.address, location: la.location };
  }

  private handleRemoveAddress(idx: number) {
    this.stops.splice(idx, 1);
    this.updateStopsMutation();
  }

  private handleAddAddress() {
    if (this.stops.length < stopsLimit) {
      this.stops = [ ...this.stops, null ];
      this.updateStopsMutation();
    }
  }

  private handleAddressChange(la: LocationAddress, idx: number) {
    this.stops[idx] = this.locationAddressToTripStop(la);
    this.updateStopsMutation();
  }

  private async handleLocationButton(location: Location, idx: number) {
    this.stops[idx] = await this.changeAddress(location);
    this.updateStopsMutation();
  }

  private async changeAddress(location: Location) {
    const results = await this.fetchPlaceNames({ longitude: location.longitude, latitude: location.latitude,  poi: true })

    if (results && results.features && results.features[0]) {
      return {
        address: featureToAddress(results.features[0]),
        location: featureToLocation(results.features[0])
      };
    }
  }

  private updateStopsMutation() {
    const copy = deepCopy(this.tripMutation);
    copy.stops = this.stops;
    this.$emit('input', copy);
  }

  private updateStops() {
    if (this.tripMutation && this.tripMutation.stops) {
      this.stops = deepCopy(this.tripMutation.stops);
    }
  }

  private emitEdit(value: string) {
    this.$emit('toggleEditMode', value);
  }
}
